import { useCallback, useEffect, useState } from 'react';
import { api, type SuppressorRow } from '../lib/api.js';
import { SuppressorEditor } from '../components/SuppressorEditor.js';

const PAGE_SIZE = 50;

export function SuppressorsPage(): JSX.Element {
  const [items, setItems] = useState<SuppressorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [brand, setBrand] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [brands, setBrands] = useState<string[]>([]);
  const [editing, setEditing] = useState<SuppressorRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.brands().then(setBrands).catch(() => undefined);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query: Parameters<typeof api.suppressors.list>[0] = { limit: PAGE_SIZE, offset };
      if (q) query.q = q;
      if (brand) query.brand = brand;
      if (includeInactive) query.is_active = '0';
      const r = await api.suppressors.list(query);
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [q, brand, includeInactive, offset]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      <h1>Suppressors</h1>
      {error && <div className="notice error">{error}</div>}

      <div className="toolbar">
        <input
          className="input"
          placeholder="Search brand / model / SKU"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOffset(0); }}
        />
        <select className="select" value={brand} onChange={(e) => { setBrand(e.target.value); setOffset(0); }}>
          <option value="">All brands ({brands.length})</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--muted)' }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => { setIncludeInactive(e.target.checked); setOffset(0); }}
          /> include inactive
        </label>
        <button className="primary" onClick={() => setCreating(true)} style={{ marginLeft: 'auto' }}>New suppressor</button>
      </div>

      <table>
        <thead>
          <tr>
            <th>Brand</th>
            <th>Model</th>
            <th>Cover</th>
            <th>Dims</th>
            <th>SKU</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id}>
              <td>{r.brand}</td>
              <td>{r.model}</td>
              <td>{r.cover_type}</td>
              <td>Ø{r.suppressor_diameter}" × {r.suppressor_length}"</td>
              <td className="sku">{r.base_sku}</td>
              <td>
                {r.flow_through ? <span className="badge">Flow-through</span> : null}{' '}
                {r.needs_review ? <span className="badge review">Review</span> : null}{' '}
                {r.is_active === 0 ? <span className="badge inactive">Inactive</span> : null}
              </td>
              <td>
                <button onClick={() => setEditing(r)}>Edit</button>{' '}
                {r.is_active !== 0 && (
                  <button className="danger" onClick={async () => {
                    if (!confirm(`Soft-delete ${r.brand} ${r.model}?`)) return;
                    await api.suppressors.softDelete(r.id);
                    void refresh();
                  }}>Soft delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <span>{loading ? 'Loading…' : `Showing ${items.length} of ${total}`}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
          <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      </div>

      {creating && (
        <SuppressorEditor
          initial={{ cover_type: 'CUSTOM HEAVY' }}
          onCancel={() => setCreating(false)}
          onSave={async (changes) => {
            await api.suppressors.create(changes);
            setCreating(false);
            void refresh();
          }}
        />
      )}
      {editing && (
        <SuppressorEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={async (changes) => {
            await api.suppressors.update(editing.id, changes);
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </>
  );
}
